import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react'
import { useUpdateUser } from 'queries/user.query'

// Validation helper functions
const isValidUsername = username => /^[a-zA-Z0-9 ]{3,}$/.test(username)
const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
const isValidPassword = password => password.length >= 8

export const EditUserModal = ({ isOpen, onClose, user }) => {
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    role: 'admin',
    password: '',
    confirmPassword: '',
  })

  const [formErrors, setFormErrors] = useState({})
  const [passwordMatchError, setPasswordMatchError] = useState('')
  const toast = useToast()
  const { mutate: updateUser, isPending } = useUpdateUser()

  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')

  useEffect(() => {
    if (user) {
      setEditFormData({
        username: user.username || '',
        email: user.email || '',
        role: user.role || 'admin',
        password: '',
        confirmPassword: '',
      })
      setFormErrors({})
      setPasswordMatchError('')
    }
  }, [user])

  const handleInputChange = e => {
    const { name, value } = e.target
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateForm = () => {
    const errors = {}

    if (!isValidUsername(editFormData.username)) {
      errors.username = 'Username must be at least 3 characters long.'
    }

    if (!isValidEmail(editFormData.email)) {
      errors.email = 'Please enter a valid email address.'
    }

    if (editFormData.password && !isValidPassword(editFormData.password)) {
      errors.password = 'Password must be at least 8 characters long.'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleUpdateUser = e => {
    e.preventDefault()
    setPasswordMatchError('')

    if (!validateForm()) {
      return
    }

    if (editFormData.password !== editFormData.confirmPassword) {
      setPasswordMatchError('Passwords do not match.')
      return
    }

    const updatedUserData = {
      username: editFormData.username,
      email: editFormData.email,
    }

    if (editFormData.password) {
      updatedUserData.password = editFormData.password
    }

    updateUser(
      { id: user.id, updatedUserData },
      {
        onSuccess: () => {
          onClose()
          toast({
            title: 'User Updated.',
            description: 'The new user has been successfully updated.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
        },
        onError: () => {
          toast({
            title: 'Failed to Update User.',
            description:
              // err.response?.data?.message ||
              'There was an error updating the user.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
        },
      },
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit User</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleUpdateUser}>
            <FormControl isRequired isInvalid={formErrors.username}>
              <FormLabel>Name</FormLabel>
              <Input
                type='text'
                name='username'
                value={editFormData.username}
                onChange={handleInputChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              {formErrors.username && (
                <Text color='red.500' fontSize='sm' mt={1}>
                  {formErrors.username}
                </Text>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={formErrors.email}>
              <FormLabel>Email</FormLabel>
              <Input
                type='email'
                name='email'
                value={editFormData.email}
                onChange={handleInputChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              {formErrors.email && (
                <Text color='red.500' fontSize='sm' mt={1}>
                  {formErrors.email}
                </Text>
              )}
            </FormControl>

            <FormControl isRequired mt={4}>
              <FormLabel>Role & Permissions</FormLabel>
              <Select
                name='role'
                value={editFormData.role}
                onChange={handleInputChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              >
                <option value='admin'>Admin (Full System Access)</option>
                <option value='gate_keeper'>Gate Keeper (Ticket Scanner Only)</option>
              </Select>
              <Text color='gray.500' fontSize='xs' mt={1}>
                {editFormData.role === 'gate_keeper'
                  ? 'Gate Keepers only have access to the mobile ticket scanner and cannot view financial or system data.'
                  : 'Admins have full access to events, seating, vouchers, and financial reports.'}
              </Text>
            </FormControl>

            <FormControl isInvalid={formErrors.password}>
              <FormLabel>Password</FormLabel>
              <Input
                type='password'
                name='password'
                value={editFormData.password}
                onChange={handleInputChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              {formErrors.password && (
                <Text color='red.500' fontSize='sm' mt={1}>
                  {formErrors.password}
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Confirm Password</FormLabel>
              <Input
                type='password'
                name='confirmPassword'
                value={editFormData.confirmPassword}
                onChange={handleInputChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              {passwordMatchError && (
                <Text color='red.500' fontSize='sm' mt={1}>
                  {passwordMatchError}
                </Text>
              )}
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme='blue'
            type='submit'
            isLoading={isPending}
            onClick={handleUpdateUser}
            isDisabled={isPending}
          >
            Save Changes
          </Button>
          <Button ml={3} colorScheme='gray' onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
